import type { ActiveDiscountOffer } from '@/lib/discount-offers';
import type { DebtData } from '@/types';

/**
 * Mock data for preview mode
 * Uses centralized mock data from shared package, filtered by context
 */

// Import mock data from shared package
import {
  getMockDebtsForContext,
  getMockDebts as getSharedMockDebts,
  MOCK_DEBT_ID,
  MOCK_DEBT_ID_PAYING,
  MOCK_DEBT_ID_PLAN_ACTIVE,
  MOCK_DEBT_ID_RESOLVED,
} from '../../../../packages/shared/src/mock-data/index';

// Re-export constants
export { MOCK_DEBT_ID, MOCK_DEBT_ID_PAYING, MOCK_DEBT_ID_PLAN_ACTIVE, MOCK_DEBT_ID_RESOLVED };

/**
 * Get mock debts - uses context from sessionStorage (set by hub via postMessage)
 * Falls back to default operator debts if no context is provided
 *
 * Note: Cast is needed because shared package uses `unknown` for fullPlan
 * to avoid circular dependencies, but customer-portal expects OphelosPaymentPlanResponse
 */
export function getMockDebts(): DebtData[] {
  // Try to get context from sessionStorage (set by hub)
  try {
    const contextStr = sessionStorage.getItem('hub_preview_context');
    if (contextStr) {
      const context = JSON.parse(contextStr) as { operatorName: string; portfolioName?: string; clientName?: string };
      if (context.operatorName) {
        return getMockDebtsForContext(context.operatorName, context.portfolioName, context.clientName) as DebtData[];
      }
    }
  } catch (e) {
    console.warn('Failed to load context from sessionStorage:', e);
  }

  // Fallback to default operator debts
  return getSharedMockDebts() as DebtData[];
}

/**
 * Sample Multibanco balance reference for preview/demo mode, so an operator
 * previewing the journey in Hub sees the same card a customer holding a code
 * would see. Expiry is relative so the preview never shows a lapsed code.
 */
export function getMockBalanceReference() {
  const expires = new Date();
  expires.setDate(expires.getDate() + 30);
  // Mirrors the engine: the code covers every debt the customer has NOT already
  // engaged on, so a debt under an active plan is left out of the amount.
  const debts = getMockDebts();
  const payable = debts.filter((debt) => !debt.paymentPlan);
  return {
    reference: {
      entity: '21367',
      reference: '482 619 037',
      amount_subunits: payable.reduce((total, debt) => total + Math.round(debt.currentBalance * 100), 0),
      currency: 'EUR',
      expires_at: expires.toISOString(),
    },
    excludes_arranged_cases: payable.length < debts.length,
  };
}

/**
 * One debt's codes for preview/demo mode. Unlike the account reference this is
 * per case, so the amount is that debt's own balance — and a debt already under
 * a payment plan has no code at all, mirroring the engine's `not_payable`.
 */
export function getMockCaseReference(caseId: string) {
  const debt = getMockDebts().find((d) => d.id === caseId);
  if (!debt || debt.paymentPlan) {
    return { reference: null, partial_references: [], payable_amount_subunits: null };
  }
  const expires = new Date();
  expires.setDate(expires.getDate() + 30);
  const amountSubunits = Math.round(debt.currentBalance * 100);
  return {
    reference: {
      entity: '21367',
      reference: '571 204 883',
      amount_subunits: amountSubunits,
      currency: 'EUR',
      expires_at: expires.toISOString(),
    },
    partial_references: [],
    payable_amount_subunits: amountSubunits,
  };
}

/** A dummy live offer for the previewer's "show discounts" toggle: 20% off pay now, 10% off a plan, for a week. */
export function getMockOffers(debts: DebtData[]): Record<string, ActiveDiscountOffer> {
  let showDiscounts = false;
  try {
    showDiscounts = JSON.parse(sessionStorage.getItem('hub_preview_context') ?? '{}').showDiscounts === true;
  } catch {
    // no context: no offers
  }
  if (!showDiscounts) return {};
  const expiresAt = new Date(Date.now() + 7 * 86_400_000).toISOString();
  return Object.fromEntries(
    debts
      .filter((d) => d.currentBalance > 0)
      .map((d) => [
        d.id,
        {
          offerId: `offer_mock_${d.id}`,
          campaignId: 'campaign_mock',
          caseId: d.id,
          status: 'eligible',
          configSnapshot: {
            starts_at: new Date().toISOString(),
            ends_at: expiresAt,
            options: {
              pay_now: { enabled: true, percentage: 0.2, max_off_millicents: null },
              payment_plan: { enabled: true, percentage: 0.1, max_off_millicents: null },
            },
          },
          expiresAt,
        },
      ]),
  );
}
